import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import config from '../config';
import loadingIcon from '../images/loading.svg';
import { useTranslation } from 'react-i18next';
import { PRODUCT_CATEGORIES } from '../constants/productCategories';
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableGalleryItem({ item, index, disabled, onDelete }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
        disabled
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <li ref={setNodeRef} style={style}>
            <div className='updateProduct-gallery-drag-handle' {...attributes} {...listeners}>⠿</div>
            <img src={item.imageUrl} alt={`Gallery ${index}`} />
            <button
                type='button'
                disabled={disabled}
                onClick={() => onDelete(item.id)}
            >
                {t('updateForm.galleryDelete', 'Delete')}
            </button>
        </li>
    );
}

export default function UpdateProductForm() {
    const { t } = useTranslation();
    const [newImage, setNewImage] = useState(null);
    const [newGalleryImages, setNewGalleryImages] = useState([]);
    const [product, setProduct] = useState({});
    const [loading, setLoading] = useState(false);
    const [pendingGalleryDeletes, setPendingGalleryDeletes] = useState([]);
    const [galleryOrderChanged, setGalleryOrderChanged] = useState(false);
    const [error, setError] = useState(null);
    const { id } = useParams();
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
    );

    useEffect(() => {
        axios.get(`${config.API_URL}/api/product/get/${id}`)
            .then(response => setProduct(response.data))
            .catch(console.error);
    }, [id]);

    function handleInputChange(e) {
        const { name, value, type, checked } = e.target;
        setProduct(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    function handleDeleteGalleryImage(galleryId) {
        if (loading) return;
        if (!window.confirm(t('updateForm.galleryDeleteConfirm', 'Delete this photo?'))) return;

        setProduct(prev => ({
            ...prev,
            gallery: prev.gallery.filter(item => item.id !== galleryId)
        }));
        setPendingGalleryDeletes(prev => [...prev, galleryId]);
    }

    function handleGalleryDragEnd(event) {
        if (loading) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const currentGallery = product.gallery || [];
        const oldIndex = currentGallery.findIndex(item => item.id === active.id);
        const newIndex = currentGallery.findIndex(item => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const gallery = arrayMove(currentGallery, oldIndex, newIndex);
        setProduct(prev => ({ ...prev, gallery }));
        setGalleryOrderChanged(true);
    }

    async function handleUpdateProduct(e) {
        e.preventDefault();
        setError(null);

        const formData = new FormData();
        formData.append("name", product.name);
        formData.append("price", product.price);
        formData.append("priceEur", product.priceEur || '');
        formData.append("lastPrice", product.lastPrice);
        formData.append("lastPriceEur", product.lastPriceEur || '');
        formData.append("installationPrice", product.installationPrice);
        formData.append("installationPriceEur", product.installationPriceEur || '');
        formData.append("soldOut", product.soldOut ? "true" : "false");
        formData.append("category", product.category || PRODUCT_CATEGORIES[0].value);
        formData.append("description", product.description);
        formData.append("descriptionEn", product.descriptionEn || '');
        formData.append("composition", product.composition);
        formData.append("compositionEn", product.compositionEn || '');
        formData.append("demensions", product.demensions);

        if (newImage) formData.append("image", newImage);
        if (newGalleryImages.length > 0) {
            newGalleryImages.forEach(file => formData.append("gallery", file));
        }

        setLoading(true);
        try {
            const authHeader = { 'Authorization': `Bearer ${localStorage.getItem("access_tocken")}` };

            if (pendingGalleryDeletes.length > 0) {
                await Promise.all(pendingGalleryDeletes.map(galleryId =>
                    axios.delete(`${config.API_URL}/api/product/gallery/${galleryId}`, { headers: authHeader })
                ));
            }

            if (galleryOrderChanged && (product.gallery || []).length > 0) {
                const order = product.gallery.map((item, sortOrder) => ({ id: item.id, sortOrder }));
                await axios.put(`${config.API_URL}/api/product/gallery/reorder`, order, {
                    headers: { 'Content-Type': 'application/json', ...authHeader }
                });
            }

            await axios.put(
                `${config.API_URL}/api/product/update/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...authHeader
                    }
                }
            );
            setPendingGalleryDeletes([]);
            setGalleryOrderChanged(false);
        } catch (error) {
            console.error("Error updating product:", error);
            setError(error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return <div className='loading'><img src={loadingIcon} alt='none gif' /></div>;
    }

    return (
        <div className='updateProduct'>
            {error && <p className='updateProduct-error'>{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
            <form onSubmit={handleUpdateProduct}>
                <div className='updateProduct-main'>
                    <div className='updateProduct-fields'>
                        <label>{t('updateForm.name')}</label>
                        <input type="text" name="name" value={product.name || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.price')}</label>
                        <input type="number" name="price" value={product.price || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.priceEur')}</label>
                        <input type="number" name="priceEur" value={product.priceEur || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.lastPrice')}</label>
                        <input type="number" name="lastPrice" value={product.lastPrice || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.lastPriceEur')}</label>
                        <input type="number" name="lastPriceEur" value={product.lastPriceEur || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.installationPrice')}</label>
                        <input type="number" name="installationPrice" value={product.installationPrice || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.installationPriceEur')}</label>
                        <input type="number" name="installationPriceEur" value={product.installationPriceEur || ''} onChange={handleInputChange} />
                        <label>Sold out</label>
                        <input type="checkbox" name="soldOut" checked={product.soldOut || false} onChange={handleInputChange} />
                        <label>{t('updateForm.category')}</label>
                        <select name="category" value={product.category || PRODUCT_CATEGORIES[0].value} onChange={handleInputChange}>
                            {PRODUCT_CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                        <label>{t('updateForm.descriptionPl')}</label>
                        <textarea name="description" value={product.description || ''} onChange={handleInputChange} />

                        <label>{t('updateForm.descriptionEn')}</label>
                        <textarea name="descriptionEn" value={product.descriptionEn || ''} onChange={handleInputChange} />

                        <label>{t('updateForm.constructionPl')}</label>
                        <textarea name="composition" value={product.composition || ''} onChange={handleInputChange} />

                        <label>{t('updateForm.constructionEn')}</label>
                        <textarea name="compositionEn" value={product.compositionEn || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.demensions')}</label>
                        <textarea name="demensions" value={product.demensions || ''} onChange={handleInputChange} />
                        <label>{t('updateForm.image')}</label>
                        <input type='file' onChange={e => setNewImage(e.target.files[0])} />
                    </div>
                    <img src={product.image} alt="Product" />
                </div>

                <div className='updateProduct-gallery'>
                    <label>{t('updateForm.galleryAdd')}</label>
                    <input type='file' multiple onChange={e => setNewGalleryImages([...e.target.files])} />
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGalleryDragEnd}>
                        <SortableContext items={(product.gallery || []).map(item => item.id)} strategy={rectSortingStrategy}>
                            <ul>
                                {product.gallery?.map((item, index) => (
                                    <SortableGalleryItem
                                        key={item.id}
                                        item={item}
                                        index={index}
                                        disabled={loading}
                                        onDelete={handleDeleteGalleryImage}
                                    />
                                ))}
                            </ul>
                        </SortableContext>
                    </DndContext>
                </div>

                <button type='submit' disabled={loading}>{t('updateForm.apply')}</button>
            </form>
        </div>
    );
}
